import { Collapse, Spin } from "antd";
import React, { Fragment, useEffect, useState } from "react";
import { api } from "~/lib/api.js";
import { colors } from "~/theme/colors.js";
import { getBadgeConfig } from "./BadgeConfig.js";
import { StackedBadge } from "./BadgeLevels.jsx";
import { LevelProgress } from "./LevelProgress.jsx";
import { BadgeDetail } from "./BadgeDetail.jsx";

const chunk = (arr, n) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

/**
 * BadgesPanel — 3-column badge grid with accordion inline detail.
 *
 * Props:
 *   badges   {array|null}  Pre-loaded badges (own profile). If null, fetched via userId.
 *   userId   {number}      If provided, fetches badges for this user (instructor view).
 *   maxHeight {number}     Maximum height for the badge panel.
 */
export const BadgesPanel = ({ badges: badgesProp = null, userId = null, maxHeight = null, maxWidth = null }) => {
  const BADGE_CONFIG = getBadgeConfig();
  const ALL_BADGE_NAMES = Object.keys(BADGE_CONFIG);
  const [badges, setBadges] = useState(badgesProp || []);
  const [loading, setLoading] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState(null);
  const [achievers, setAchievers] = useState([]);
  const [latestValue, setLatestValue] = useState(null);
  const [loadingAchievers, setLoadingAchievers] = useState(false);

  // Fetch by userId (instructor view)
  useEffect(() => {
    if (!userId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get(`badges/${userId}/user.json`);
        setBadges(res || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  // Sync prop changes (dashboard passes badges directly)
  useEffect(() => {
    if (badgesProp !== null) setBadges(badgesProp);
  }, [badgesProp]);

  // Fetch achievers when a badge is selected
  useEffect(() => {
    if (!selectedBadge) {
      setAchievers([]);
      return;
    }
    const load = async () => {
      setLoadingAchievers(true);
      try {
        const res = await api.get(`badges/badge_achievers.json?name=${encodeURIComponent(selectedBadge)}&user_id=${userId || "current"}`);
        setAchievers(res.achievers || []);
        setLatestValue(res.latest_value || null);
      } finally {
        setLoadingAchievers(false);
      }
    };
    load();
  }, [selectedBadge]);

  // Build earnedByName: { name: { level: { flight_uuid, date_achieved } } }
  const earnedByName = badges.reduce((acc, b) => {
    if (!acc[b.name]) acc[b.name] = {};
    acc[b.name][b.level] = {
      flight_id: b.flight_id,
      date_achieved: b.date_achieved,
    };
    return acc;
  }, {});

  // Sort: earned first (by latest date desc), unearned last
  const allSorted = [...ALL_BADGE_NAMES].sort((a, b) => {
    const aEarned = !!earnedByName[a];
    const bEarned = !!earnedByName[b];
    if (aEarned && !bEarned) return -1;
    if (!aEarned && bEarned) return 1;
    if (aEarned && bEarned) {
      const aDate = Math.max(
        ...Object.values(earnedByName[a]).map((e) =>
          new Date(e.date_achieved).getTime(),
        ),
      );
      const bDate = Math.max(
        ...Object.values(earnedByName[b]).map((e) =>
          new Date(e.date_achieved).getTime(),
        ),
      );
      return bDate - aDate;
    }
    return 0;
  });

  const rows = chunk(allSorted, 3);

  const handleClick = (name) => {
    setSelectedBadge((prev) => (prev === name ? null : name));
  };

  if (loading)
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <Spin />
      </div>
    );

  return (
    <div>
      <div style={{ margin: '0 12px 16px 12px' }}>
        <LevelProgress earnedCount={badges.length} />
      </div>

      <div style={{ maxHeight: maxHeight || 'auto', overflowY: maxHeight ? 'auto' : 'visible' }}>

      {rows.map((row, rowIdx) => {
        const detailOpen = row.includes(selectedBadge);
        return (
          <Fragment key={rowIdx}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 12,
                marginBottom: detailOpen ? 0 : 12,
              }}
            >
              {[...row, ...Array(3 - row.length).fill(null)].map((name, i) => {
                if (!name) return <div key={`empty-${i}`} />;
                const config = BADGE_CONFIG[name];
                const earnedEntry = earnedByName[name];
                const isEarned = !!earnedEntry;
                const maxLevel = isEarned
                  ? Math.max(...Object.keys(earnedEntry).map(Number))
                  : 0;
                const isSelected = selectedBadge === name;

                return (
                  <div
                    key={name}
                    onClick={() => handleClick(name)}
                    style={{
                      cursor: "pointer",
                      padding: "6px 2px",
                      borderRadius: 2,
                      background: isSelected ? colors.gray_lightest : null,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 8,
                      textAlign: "center",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <StackedBadge
                      icon={config.icon}
                      label={config.label}
                      count={isEarned ? maxLevel : 1}
                      earned={isEarned}
                      size={128}
                      maxScreenWidth={maxWidth}
                      step={12}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: isSelected ? 700 : isEarned ? 500 : 400,
                        color: isEarned ? "#000" : "#aaa",
                        lineHeight: 1.3,
                      }}
                    >
                      {config.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Animated detail panel — ghost Collapse hides the header, provides the Ant expand animation */}
            <Collapse
              ghost
              activeKey={detailOpen ? ['detail'] : []}
              style={{ marginBottom: detailOpen ? 12 : 0 }}
              items={[{
                key: 'detail',
                label: null,
                showArrow: false,
                styles: {
                  header: { padding: 0, height: 0, minHeight: 0, overflow: 'hidden' },
                  body: { padding: 0 },
                },
                children: selectedBadge && (
                  <BadgeDetail
                    config={BADGE_CONFIG[selectedBadge]}
                    earnedLevels={earnedByName[selectedBadge] || {}}
                    achievers={achievers}
                    latestValue={latestValue}
                    loadingAchievers={loadingAchievers}
                  />
                ),
              }]}
            />
          </Fragment>
        );
      })}
      </div>
    </div>
  );
};
